/*
 * Copyright (c) 2026 Centrica.  All rights reserved.
 *
 * This source code is licensed under the license found in the
 * LICENSE file in the root directory of this source tree.
 * Date: 2026-10-07
 */
import { EnvironmentProviders, inject, makeEnvironmentProviders } from '@angular/core';
import { Router } from '@angular/router';
import {
  NEBULA_EXPORT_SOURCE_FACTORY,
  NEBULA_IMPORT_SOURCE_FACTORY,
  NEBULA_TASKS_NAVIGATOR,
  NebulaExportSourceFactory,
  NebulaImportSourceFactory,
  NebulaTasksNavigator,
} from 'nebula-ui-kit';
import { HttpService } from '../http/http.service';
import { OrigenExportacionSimappe } from './origen-exportacion-simappe';
import { OrigenImportacionSimappe } from './origen-importacion-simappe';

/** Configuracion de los dialogos genericos del kit en el ERP. */
export interface NebulaImportExportConfig {
  /**
   * Ruta del «Centro de Tareas» dentro del producto (`tareas/list`): cada producto la publica, y el
   * navegador la abre en el producto activo con `?tarea=<jobId>`.
   */
  rutaCentroTareas: string;
}

/**
 * Ruta absoluta del Centro de Tareas en el producto de la URL vigente.
 *
 * @param url URL vigente del router.
 * @param ruta ruta del Centro dentro del producto.
 * @returns `/layout/<producto>/<ruta>`, o `/layout/<ruta>` si la URL no esta en un producto.
 */
export function rutaEnProductoActivo(url: string, ruta: string): string {
  const producto = /^\/layout\/([^/?#]+)/.exec(url)?.[1];
  return producto ? `/layout/${producto}/${ruta}` : `/layout/${ruta}`;
}

/**
 * Registra UNA vez el origen por defecto de Simappe para importar y exportar, y el navegador al
 * «Centro de Tareas» (HT_ARQ_013, RN-TAR-079). Cada pantalla solo pasa su ruta al dialogo.
 *
 * @param config ruta del Centro de Tareas.
 * @returns los proveedores del entorno.
 */
export function provideNebulaImportExport(config: NebulaImportExportConfig): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: NEBULA_IMPORT_SOURCE_FACTORY,
      useFactory: (): NebulaImportSourceFactory => {
        const http = inject(HttpService);
        return <R>(ruta: string) => new OrigenImportacionSimappe<R>(http, ruta);
      },
    },
    {
      provide: NEBULA_EXPORT_SOURCE_FACTORY,
      useFactory: (): NebulaExportSourceFactory => {
        const http = inject(HttpService);
        return <R>(ruta: string) => new OrigenExportacionSimappe<R>(http, ruta);
      },
    },
    {
      provide: NEBULA_TASKS_NAVIGATOR,
      useFactory: (): NebulaTasksNavigator => {
        const router = inject(Router);
        return (jobId?: string) =>
          void router.navigate(
            [rutaEnProductoActivo(router.url, config.rutaCentroTareas)],
            jobId ? { queryParams: { tarea: jobId } } : {},
          );
      },
    },
  ]);
}
